import { useDispatch } from "react-redux";
import { setOpenAmberCopilot } from "@Pages/PropertyPage/components/ZendeskChat/ChatBotReducer";
import CustomLink from "@Components/CustomLink";
import Reveal from "@Components/Reveal/Reveal";
import styles from "./Channels.module.scss";
// The WhatsApp bubble — flat #59d96b with a white handset, no gradient.
//
// It replaced the app TILE (a squircle with a gradient baked in), and that changed how it is
// rendered: a bubble has no background of its own, so it goes in the `mark` slot alongside the
// line icons instead of standing in for the slot. It also must NOT be clipped to a circle the way
// the tile was — the bubble's tail is part of its silhouette.
//
// Still an asset rather than inlined JSX: it carries `id="whatsapp"`, and an inlined id is a
// silent collision waiting for a second one on the page. As an `<img>` its ids are scoped to their
// own document.
import whatsappMark from "../../assets/whatsapp.svg";
// Exported from the design (node I2306:4085;244:3140) rather than drawn by hand, so the stroke
// weight and the taper are the designer's and not an approximation. Its fill is baked in at
// #6B7280 — the file's Secondary/neutral/500 token — which is why the chevron does not recolour
// on hover the way the hand-drawn one did; it only nudges.
import chevronIcon from "../../assets/chevron.svg";
// amber Copilot's mark, lifted from amber-user-website (src/icons/AmberCopilotLogoSvg.tsx).
//
// Converted to a standalone .svg rather than copied in as the JSX component it is there, for the
// same reason as WhatsApp: it carries hardcoded ids — `mask0_2926_1929`, `mask1_2926_1929`,
// `filter0_f_2926_1929`, `clip0_2926_1929` — and inlining those into a page that already has one,
// or later gains one, silently repaints the mark through the wrong mask. As an `<img>` its ids are
// scoped to their own document and cannot collide with anything.
import copilotMark from "../../assets/amber-copilot.svg";
// Supplied line icons, drawn on a 24 grid with `stroke="black"` baked in.
//
// These replaced a Font Awesome Pro `jelly-fill` set (envelope / phone-volume / book), which was
// solid rather than outlined — at 30px the filled envelope in particular read as a blob rather
// than an envelope, because that family trades detail for weight. Line icons hold their shape at
// this size, and they match the set now on How It Works, so the two pages read as one system.
//
// Their viewBoxes are not all square (24x25, 24x24, 25x24), which is why `.mark` carries
// `object-fit: contain` — it letterboxes rather than distorting them into a square box.
import emailMark from "../../assets/email-ground.svg";
import phoneMark from "../../assets/phone-ringing.svg";
import bookMark from "../../assets/book-open.svg";
import wrapperHOC from "@Utils/wrapperHOC";
import Image from "@Components/Image";
import useTranslationHook from "hooks/useTranslationHook";
import useCountryContactInfo from "hooks/useCountryContactInfo";
import { EMAIL_1, PHONE_5 } from "constants/configConstants";
import config from "@Config/index";
/** The knowledge base this page's Help centre row opens. See the note on `GUIDES`. */
const HELP_CENTRE_URL = "https://help.amberstudent.com/portal/en/kb/amberstudent";

// ⚠️  THE PHONE NUMBER IS GEO-SELECTED, so there is no constant for it here.
//
// The page this replaced rendered Home's `GetHelpDesktop`, which takes the number from
// `useCountryContactInfo()`: CloudFront's `cloudfront-viewer-country` header lands in
// `appData.country`, and `addCountryWiseContactInfo` switches on it — AU to +61, US to
// +1, IN to +91, everyone else the UK line. Hardcoding any one of those shows most of
// the world a number that is not theirs, so the row takes the hook's value and this
// file names no phone number at all.
//
// WhatsApp and email are NOT geo-selected and never were: one UK WhatsApp line and one
// address, exactly as the old page had them.
const SUPPORT_EMAIL = EMAIL_1;
// WhatsApp is a SEPARATE line from the phone number above. The two used to share one
// constant, which is why both rows showed the same digits. The link is the one support
// hands out, kept verbatim rather than rebuilt as a wa.me URL.
const WHATSAPP_PHONE = "+44 7888 863490";
const WHATSAPP_URL =
  "https://api.whatsapp.com/send/?phone=447888863490&text&type=phone_number&app_absent=0";

/**
 * Support channels — Figma node 2306:4048 ("Group — Consumer Support Channels").
 *
 * Built to that node's metrics: a small-caps eyebrow over a stack of 10px-spaced rows, each a
 * 14px-radius card with 16/20px padding, the copy taking the slack and a trailing badge and
 * chevron. Every value below that looks arbitrary came from the node.
 *
 * ── Where this deviates from the node, and why ───────────────────────────────
 * Three of them, all places where following the file literally would ship something broken:
 *
 *   · ICONS. The node has no icon slot at all. They are kept because they were asked for
 *     explicitly and the WhatsApp mark was supplied as an asset — and because an icon is
 *     additive here: the node's copy block is `flex: 1 0 0`, so a leading icon changes nothing
 *     else about the row. Say the word and they come out.
 *   · PLACEHOLDER VALUES. The node gives "Live Chat" and "Help Centre" the support phone
 *     number, which neither has. Their real descriptions are kept.
 *   · THE SECOND EYEBROW. Both groups in the node are labelled "SUPPORT CHANNELS"; the second
 *     holds only the help centre, which is not a support channel. Relabelled.
 *
 * The node's badge assignment IS followed exactly, including the part that looks like an
 * oversight: only Live Chat and WhatsApp carry "Instant Reply", not Phone Call. That reads as
 * deliberate on a second look — a call is not a *reply*.
 */

interface Channel {
  /**
   * Stable identifier for the row's React key and test id.
   *
   * These used to be derived from `label`, which is a translation key now — so a
   * `contact.v2.channels.email.label` (or, once resolved, a localised word) would
   * have ended up inside the selector.
   */
  id: string;
  /** Translation key for the row's name. */
  label: string;
  /**
   * The address or number, verbatim — or a translation key for a line saying what
   * the row is. Addresses and phone numbers are not translated, so `t()` falls
   * through and returns them unchanged.
   */
  detail: string;
  /** Translation key. Per the node: messaging channels only. */
  meta?: string;
  /**
   * Where the row goes.
   *
   * Every row is now a target: four carry an `href`, and `Live chat` carries `opensCopilot`
   * instead because its destination is a panel on this page rather than a URL.
   *
   * `Help centre` used to be unwired too. It now points straight at the knowledge base.
   */
  href?: string;
  /**
   * Opens the amber Copilot side panel instead of navigating.
   *
   * Copilot IS the live chat here, and it is a Redux-driven panel, not a URL — so this row
   * renders as a <button> that dispatches the same `setOpenAmberCopilot(true)` the floating
   * Copilot button and the hero's docked input already dispatch. All three entry points land
   * on one action, and `AmberCopilotWrapper` (mounted by ContactUsV2) is what listens.
   */
  opensCopilot?: boolean;
  /**
   * Exactly one of these per row.
   *
   * There was a third case, `tile`, for finished artwork carrying its OWN background — the WhatsApp
   * app tile. It is gone with that tile: a tile has to replace the icon slot rather than sit in it,
   * or you get two grounds stacked, and nothing supplied since has been one. Re-add it if a square
   * app icon ever turns up.
   *
   *   · `icon` — a monoline glyph drawn to this file's shared weight. Needs the tinted ground
   *     `.iconWrap` provides, or it floats in a white gap.
   *   · `mark` — artwork with no background of its own, rendered as an image rather than stroked
   *     paths. Every row uses this now, including WhatsApp.
   *
   * Every row now carries a real supplied asset — four `mark`s and one `tile`. Nothing is drawn in
   * this file any more, which is why `icon` has no users: it is kept because it is the only one of
   * the three that can inherit `currentColor`, so it is what a future one-off would use rather
   * than a reason to commit a file.
   */
  icon?: JSX.Element;
  mark?: string;
}

/** Order follows the node: Live Chat, WhatsApp, Email, Phone Call. */
const CHANNELS: Channel[] = [
  {
    id: "live-chat",
    label: "contact.v2.channels.livechat.label",
    // Copilot IS the live chat on amber, so the row names the thing the reader will actually meet
    // rather than describing it generically.
    detail: "contact.v2.channels.livechat.detail",
    meta: "contact.v2.channels.instantreply",
    mark: copilotMark,
    opensCopilot: true,
  },
  {
    id: "whatsapp",
    label: "contact.v2.channels.whatsapp.label",
    detail: WHATSAPP_PHONE,
    meta: "contact.v2.channels.instantreply",
    href: WHATSAPP_URL,
    mark: whatsappMark,
  },
  {
    id: "email",
    label: "contact.v2.channels.email.label",
    detail: SUPPORT_EMAIL,
    href: `mailto:${SUPPORT_EMAIL}`,
    mark: emailMark,
  },
];

/**
 * The phone row, built per request rather than at module scope.
 *
 * `useCountryContactInfo` already returns the label and `tel:` link in the shape the footer
 * and Home's help block use, so this is a thin wrapper over it rather than a second copy of
 * the country switch. Renders nothing if the hook has no number — it falls back to the UK
 * line, so that only happens if the constants are emptied.
 */
const usePhoneChannel = (): Channel | null => {
  const { phoneContact } = useCountryContactInfo();

  // For the China app, always use the +86 number regardless of detected region.
  // The geo-selected hook would otherwise show a UK or India number to Chinese users.
  if (config.IS_CHINA_APP) {
    return {
      id: "phone-call",
      label: "contact.v2.channels.phone.label",
      detail: `${PHONE_5.country_code} ${PHONE_5.number}`,
      href: `tel:${PHONE_5.full_number.replace(/\s/g, "")}`,
      mark: phoneMark,
    };
  }

  if (!phoneContact?.label || !phoneContact?.link) return null;
  return {
    id: "phone-call",
    label: "contact.v2.channels.phone.label",
    detail: phoneContact.label,
    // `addCountryWiseContactInfo` builds these as "tel: +44 …" — a space after the scheme
    // and spaces in the number. Harmless in an anchor, but stripped so the href matches
    // the shape of the mailto beside it.
    href: phoneContact.link.replace(/\s/g, ""),
    mark: phoneMark,
  };
};

/** Its own group in the node, with its own eyebrow — self-serve rather than a channel. */
const GUIDES: Channel = {
  id: "help-centre",
  label: "contact.v2.channels.helpcentre.label",
  detail: "contact.v2.channels.helpcentre.detail",
  // The knowledge base itself, not `HELP_URL`.
  //
  // `/help` was the first thing tried here — it is the constant the header's Support menu
  // uses — but it only redirects as far as `/portal/en/kb`, the portal's root. This lands
  // on the amberstudent space inside it, which is the article set this row's copy is
  // promising ("booking, payments, documents and moving in"). Verified 200 with no
  // onward redirect, so nothing is lost by naming it directly.
  href: HELP_CENTRE_URL,
  mark: bookMark,
};

/** One frame for every glyph: same box, same weight, same joins. */
const Glyph = ({ children }: { children: JSX.Element }) => (
  <svg
    className={styles.icon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

/**
 * A row, as a link where it has somewhere to go and as a plain element where it does not.
 *
 * EVERY row shows a chevron, by request — including the two that have nowhere to go yet.
 *
 * It was hidden on those, on the reasoning that a chevron is an affordance and drawing one on an
 * unwired row is what makes an unfinished row look finished. That risk is now down to one row:
 * Help centre is wired, so only Live chat still looks clickable without being so. The chevron was
 * never *absent* though, only invisible — it has to hold its slot either way, because the badge
 * sits beside it and dropping the element lets the badge slide right, which is what put three
 * identical badges at two different x positions.
 */
const Row = ({ channel, testId }: { channel: Channel; testId: string }) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const { label, detail, meta, href, icon, mark, opensCopilot } = channel;

  const body = (
    <>
      <span className={styles.iconWrap}>
        {mark ? (
          <Image src={mark} alt="" width={40} height={40} className={styles.mark} isNotLazy />
        ) : null}
        {icon ? <Glyph>{icon}</Glyph> : null}
      </span>

      <span className={styles.copy}>
        <span className={styles.name}>{t(label)}</span>
        {/* An address or phone number passes through t() unchanged — only the
            rows whose detail is a sentence carry a key. */}
        <span className={styles.detail}>{t(detail)}</span>
      </span>

      <span className={styles.trailing}>
        {meta ? <span className={styles.meta}>{t(meta)}</span> : null}
        <Image
          src={chevronIcon}
          alt=""
          width={24}
          height={24}
          className={styles.chevron}
          isNotLazy
        />
      </span>
    </>
  );

  // A <button>, not an anchor with a fake href: the target is a panel on this page, so there
  // is nothing to put in `href` and nothing to open in a new tab. It keeps `.card` for the
  // shape and picks up the same hover treatment (see `button.card` in the stylesheet).
  if (opensCopilot) {
    return (
      <li className={styles.cardItem}>
        <button
          type="button"
          className={styles.card}
          onClick={() => dispatch(setOpenAmberCopilot(true))}
          data-testid={testId}
        >
          {body}
        </button>
      </li>
    );
  }

  if (!href) return <li className={styles.card}>{body}</li>;

  // Only http(s) links should open in a new tab. mailto:/tel: hand off to the OS, so a new tab
  // would leave an empty window behind.
  const isWebLink = /^https?:\/\//i.test(href);

  return (
    <li className={styles.cardItem}>
      <CustomLink
        href={href}
        className={styles.card}
        // CustomLink only auto-detects http(s) as external, so mailto:/tel: would otherwise be
        // routed through react-router and rendered as "/mailto:…".
        isExternal
        {...(isWebLink && { target: "_blank", rel: "noopener noreferrer" })}
        dataTestId={testId}
      >
        {body}
      </CustomLink>
    </li>
  );
};

const slug = (id: string) => `contact-us-v2-card-${id}`;

const Channels = () => {
  const { t } = useTranslationHook();
  const phoneChannel = usePhoneChannel();

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <Reveal className={styles.group}>
          <span className={styles.eyebrow}>{t("contact.v2.channels.eyebrow.support")}</span>
          <ul className={styles.grid}>
            {CHANNELS.map((channel) => (
              <Row key={channel.id} channel={channel} testId={slug(channel.id)} />
            ))}
            {phoneChannel && <Row channel={phoneChannel} testId={slug(phoneChannel.id)} />}
          </ul>
        </Reveal>

        <Reveal className={styles.group} delay={80}>
          <span className={styles.eyebrow}>{t("contact.v2.channels.eyebrow.guides")}</span>
          <ul className={styles.grid}>
            <Row channel={GUIDES} testId={slug(GUIDES.id)} />
          </ul>
        </Reveal>
      </div>
    </section>
  );
};

export default wrapperHOC(Channels, {
  componentName: "Channels-ContactUsV2Card",
  showForChina: true,
});
